import { Directive, Inject, Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { AcumaticaCustomField, GetAcumaticaFields } from "../acumatica-field-decorator";
import { AcumaticaLoadingOptions } from "../loading-indicator/AcumaticaLoadingOptions";
import { AcumaticaRequestOptions, AcumaticaService } from "./../acumatica.service";

export abstract class AcumaticaEntityService {
  _id?: string;
  _apiCache: any = {};

  constructor(
    protected acumatica: AcumaticaService,
    entityName: string,
    entityType: any,
  ) {
    this._entityName = entityName;
    this._entityType = entityType;
  }

  private _entityName: string;
  public get entityName() { return this._entityName; }
  private _entityType: any;
  public get entityType() { return this._entityType; }

  protected getEntity(
    key: string | string[],
    acumaticaRequestOptions?: AcumaticaRequestOptions
  ): Observable<any> {
    return new Observable<any>(subscriber => {
      // get the cacheEntry if there is one
      let cacheToken = 'GET:' + (Array.isArray(key) ? key.join('/') : key) + this.cacheToken(acumaticaRequestOptions);
      if (acumaticaRequestOptions?.noCache || !this.acumatica.cacheAPIResponses) delete this._apiCache[cacheToken];
      let cacheEntry = this._apiCache[cacheToken];

      // when there is
      if (cacheEntry) {
        // if it's already completed
        if (cacheEntry.complete) {
          // immediately return the result or error
          if (cacheEntry.result) subscriber.next(cacheEntry.result);
          if (cacheEntry.error) subscriber.error(cacheEntry.error);
          subscriber.complete();
        } else {
          // otherwise, queue this subscriber
          cacheEntry.subscribers.push(subscriber);
        }
        return;
      }

      // when there isn't a cacheEntry for this request already
      // create a new cacheEntry
      cacheEntry = this._apiCache[cacheToken] = {
        complete: false,
        subscribers: [subscriber]
      };
      // initiate the API request
      this.acumatica.get(this, key, acumaticaRequestOptions)
        .pipe(
          // convert acumatica JSON plain object to discreet object instance & apply conversions
          map(
            (entity: any) => this.decode(entity, new this.entityType())
          )
        ).subscribe({
          next: (result: any) => {
            // cache the result
            cacheEntry.result = result;
            cacheEntry.complete = true;
            // and send it to any subscribers
            let sub;
            while (sub = cacheEntry.subscribers.shift()) {
              sub.next(result);
              sub.complete();
            }
            delete cacheEntry.subscribers;
          },
          error: (error: any) => {
            cacheEntry.error = error;
            cacheEntry.complete = true;
            subscriber.error(error);
            subscriber.complete();
          }
        });
    });
  }

  protected getEntities<T = any>(filter?: string, options?: AcumaticaRequestOptions): Observable<T[]> {
    return new Observable<any[]>(subscriber => {
      options = options || {};
      if (filter) options.filter = filter;
      this.acumatica.getEntities(this, options)
        .pipe(
          map(
            (entities: any[]) => entities.map(entity => this.decode(entity, new this.entityType()) as T)
          )
        ).subscribe(subscriber);
    });
  }

  protected putEntity(
    entity: AcumaticaEntity,
    acumaticaRequestOptions?: AcumaticaRequestOptions
  ): Observable<any> {
    return this.putToEntityName(this.entityName, entity, acumaticaRequestOptions)
  }

  protected putToEntityName(
    entityName: string,
    entity?: AcumaticaEntity,
    acumaticaRequestOptions?: AcumaticaRequestOptions
  ): Observable<any> {
    if (entity !== undefined) entity = AcumaticaEntity.encode(entity);
    return this.acumatica.put(entityName, entity, acumaticaRequestOptions)
      .pipe(
        map((entity: any) => this.decode(entity, new this.entityType()))
      );
  }

  protected action(
    entity: AcumaticaEntity,
    action: string,
    parameters: any = null,
    loader_options: AcumaticaLoadingOptions | null = {}
  ): Observable<void> {
    return this.acumatica.action(this.entityName, action, entity, parameters, loader_options);
  }

  cacheToken(options?: AcumaticaRequestOptions): string {
    if (!options) return '';
    // include any fields that can change the result of the API request
    return JSON.stringify({
      select: options.select,
      filter: options.filter,
      expand: options.expand,
      custom: options.custom,
      skip: options.skip,
      top: options.top
    });
  }

  // iterate data from the provided Acumatica resource entity (plain JSON object)
  // copy the fields & values to a new object instance of this._entityType
  // extract nested {value:..} field values and prefix top-level fields with '_'
  // apply field-specific Rules as appropriate  
  private decode(
    resource: any,
    target: any = undefined
  ): any {
    // get data defined by @AcumaticaField() decorators
    let AcumaticaFields = GetAcumaticaFields(target);

    // function to add value to target object under fieldName applying 'decoding' operations
    let importField = (target: any, acumaticaFieldName: string, value: any) => {
      // target object output field name
      let internalFieldName = AcumaticaFields?.rename_external?.[acumaticaFieldName] || acumaticaFieldName;
      // only import fields that have @Acumatica decorator or 'special' API fields (id, rowNumber, etc)
      let doImportDataField = (AcumaticaFields.fields && AcumaticaFields.fields.indexOf(internalFieldName) > -1)
        //    or if we're importing directly to a generic AcumaticaEntity object
        || (target.__proto__ === AcumaticaEntity.prototype);

      // for nested Detail array collections
      if (Array.isArray(value)) {
        if (!doImportDataField) return;
        // create a destination array (unless it already exists -- merge array collections by outputting to same name)
        target[internalFieldName] = target[internalFieldName] || [];
        // iterate the array elements
        value.forEach(element => {
          // create a new entity of appropriate type for the nested detail
          let detailEntity: any;
          if (AcumaticaFields.ctor && AcumaticaFields.ctor[internalFieldName]) {
            detailEntity = this.decode(element,
              new AcumaticaFields.ctor[internalFieldName](this.acumatica)
            );
          } else {
            // create generic AcumaticaEntity if not specified
            detailEntity = this.decode(element, new AcumaticaEntity());
          }
          // add it to the details collection
          target[internalFieldName].push(detailEntity);
        });

        // standard data field
      } else if (typeof value === 'object') {
        if (!doImportDataField) return;

        // "FieldName": { } -- null value
        if (value == null) {
          target[internalFieldName] = null;
        } else {
          switch (Object.keys(value).length) {
            case 0: // "FieldName": { } -- null value
              target[internalFieldName] = null;
              break;
            case 1: // "FieldName": {"value": ?} -- single value field
              value = Object.keys(value)[0] === "value" ? value.value : value;
              if (AcumaticaFields.reader && AcumaticaFields.reader[internalFieldName])
                value = AcumaticaFields.reader[internalFieldName].apply(resource, [value, target, resource]);
              if (value !== undefined)
                target[internalFieldName] = value;
              break;
            default: // "FieldName":{"A":1, "B":2} -- nested Related entity
              /* TODO: It is possible here for an error to be in the original entity from Acumatica
                  ? how to handle ?
                  came up after adding "Ship Via country restriction" customization when Ship Via implemented country restriction
                  the error was attached to the SalesOrder.LocationID field and came through here
                  LocationID: {value: 'MAIN', error: 'Ship Via "CUT   HOLD" can not be used for delivery to Country "CA"'}
              */
              target[internalFieldName] = this.decode(value, new AcumaticaFields.ctor[internalFieldName]());
          }
        }
      } else {
        // 'special' Acumatica API field (id, rowNumber, etc)
        target['__' + acumaticaFieldName] = value;
      }
    }

    // iterate fields in Acumatica plain JSON object entity
    for (let fieldName in resource) {
      // when $custom fields are retrieved
      // @AcumaticaField({ custom: {view: "ViewName", field: "UsrFieldName"}}) FieldName: type
      if (fieldName == 'custom') {
        // Acumatica JSON object contains custom fields in pattern of...
        /* {  ...,
         *    custom: {
         *      AcumaticaViewName: {
         *        UsrFieldName: {
         *          type: 'Custom(Type)Field',
         *          value: 'field-value'
         *        }
         *      }
         *    }
         * }
         */
        let user_fields = resource['custom'];
        for (let viewName in user_fields) {
          for (let fieldName in user_fields[viewName]) {
            let customFieldName = AcumaticaFields?.custom?.external[viewName][fieldName] || fieldName;
            let customFieldValue = user_fields[viewName][fieldName]['value'];
            importField(target, customFieldName, { value: customFieldValue });
          }
        }
      } else {
        importField(target, fieldName, resource[fieldName]);
      }
    }

    return target;
  }
}

@Directive()
export class AcumaticaEntity {
  public __id?: string;
  public __delete?: boolean;
  public __rowNumber?: number;

  // object instance method invokes static method passing self
  encode(): any { return AcumaticaEntity.encode(this); }

  private static isPlainObject = (val: any) =>
    !!val && typeof val === "object" && val.constructor === Object;

  static encode(entity: any) {
    let e = (<any>entity);
    let target: any = {};
    let value;
    let AcumaticaFields = GetAcumaticaFields(entity);

    let fields = entity instanceof AcumaticaEntity
      // iterate the @Acumatica decorated fields for the entity type
      ? AcumaticaFields?.fields
      // iterate the fields within the plain object
      : Object.keys(entity);

    // if provided a new AcumaticaEntity({init}) object instance fields will be undefined
    if (fields === undefined) fields = Object.keys(entity);

    fields.forEach((key: string) => {
      value = e[key];
      if (value === undefined) return;

      if (AcumaticaFields?.writer?.[key]) {
        value = AcumaticaFields!.writer[key].apply(entity, [value, entity]);
        if (value === undefined) return;
      } else if (value instanceof Date) {
        // value = value.toISO8601();
        value = value.format("yyyy-MM-dd");
      }

      // implement @AcumaticaField( rename );
      key = AcumaticaFields?.rename_internal?.[key] || key;

      // prepare the value
      if (Array.isArray(value)) {
        target[key] = [];
        for (let i = 0; i < value.length; i++) {
          target[key].push(AcumaticaEntity.encode(value[i]));
        }
      } else if (this.isPlainObject(value) || value instanceof AcumaticaEntity) {
        target[key] = AcumaticaEntity.encode(value);
      } else {
        let customField: AcumaticaCustomField = AcumaticaFields?.custom?.internal[key];
        if (customField) {
          console.log('TODO: implement detection of custom fields modified and then PUT');
        } else {
          target[key] = { "value": value };
        }
      }
    });

    // iterate the double-underscore-prefixed fields and add to entity
    Object.keys(entity).filter(key => key.substring(0, 2) == '__').forEach(key => {
      value = e[key];
      if (value === undefined) return;

      key = key.substring(2);
      if (key == 'delete') {
        if (value == true) target.delete = true;
        return;
      } else {
        if (AcumaticaFields?.fields === undefined || AcumaticaFields?.fields.indexOf(key) === -1) {
          // innate Acumatica field (id, rowNumber)
          target[key] = value;
          return;
        }
      }
    });

    return target;
  }

  public normalize(): any {
    let obj = Object.assign({}, this);
    Object.getOwnPropertyNames(obj).reduce((o, prop) => {
      return o;
    })

    return obj;
  }

  constructor(init?: any, defaults?: any) {
    if (defaults) Object.assign(this, defaults);
    if (init && !(init instanceof AcumaticaService)) {
      let target: any = this;
      for (var prop in init) target[prop] = init[prop];
    }
  }
}

export interface noteEntity {
  note?: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class GenericEntityService extends AcumaticaEntityService {
  constructor(
    protected acumatica: AcumaticaService,
  ) {
    super(acumatica, '', AcumaticaEntity);
  }

  inquiry(entityName: string, inquiry?: AcumaticaEntity, options?: AcumaticaRequestOptions): Observable<any> {
    return this.putToEntityName(entityName, inquiry, options);
  }
}

@Injectable({
  providedIn: 'root'
})
export class GenericInquiryService extends AcumaticaEntityService {
  constructor(
    acumatica: AcumaticaService,
    @Inject(String) entityName: string,
    @Inject(AcumaticaEntity) entityType: any = AcumaticaEntity
  ) {
    super(acumatica, entityName, entityType);
  }

  inquiry(inquiry: AcumaticaEntity, options?: AcumaticaRequestOptions): Observable<any> {
    return this.putEntity(inquiry, options);
  }
}